import { useCallback, useEffect, useState } from "react";
import LoadingPanel from "../../../shared/components/LoadingPanel";
import {
    Grid,
    GridColumn as Column,
    GridCellProps,
    GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import TextFilter from "../../../shared/grid-filters/TextFilter";
import { DEFAULT_FILTER_OPERATORS } from "../../../shared/grid-filters";
import { ErrorCenterSummary, ErrorData, GridSetup } from "../models/error-center-model";
import { DEFAULT_SORT, INITIAL_GRID_SETUP } from "../constant/error-center-default";
import { NumberFilter } from "../../../shared/grid-filters/NumberFilter";
import DateFilter from "../../../shared/grid-filters/DateFilter";
import moment from "moment";
import { resolveMessage, toObjectExpression } from "../../../shared/service/utils";
import { getErrorCenter, resendToXero } from "../api";
import { FIRST_PAGE } from "../../../shared/constants/shared-contants";
import { CustomAlert, IAlert } from "../../../shared/components/CustomAlert";
import { ErrorDetailsModal } from "./ErrorDetailsModal";

const EMPTY_SUMMARY: ErrorCenterSummary = {
    total: 0,
    mapping: 0,
    api: 0,
    config: 0,
}

const ErrorCenterTable: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState<ErrorData[]>([]);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [gridSetup, setGridSetup] = useState<GridSetup>(INITIAL_GRID_SETUP);
    const [summary, setSummary] = useState<ErrorCenterSummary>(EMPTY_SUMMARY);
    const [selectedData, setSelectedData] = useState<ErrorData | null>(null);
    const [formNotification, setformNotification] = useState<IAlert | undefined>();
    const [errorDetailsModalShow, setErrorDetailsModalShow] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    const showNotification = useCallback((notification: IAlert) => {
        setformNotification(notification);
        setTimeout(() => setformNotification(undefined), 3000);
    }, []);

    const fetchData = useCallback(() => {
        setLoading(true)
        const { sort, skip, take, filter } = gridSetup
        const data = toObjectExpression(filter)

        getErrorCenter(skip, take, sort[0], data)
            .then((response: any) => {
                setTableData(response.data.errorCenters);
                setTotalRows(response.data.summary.total)
                setSummary(response.data.summary || EMPTY_SUMMARY)
            })
            .catch((error: any) => {
                showNotification({
                    message: resolveMessage(error, "Unable to load error send draft invoices"),
                    header: "Error Send Draft Invoices",
                    type: 'danger'
                });
            })
            .finally(() => setLoading(false))
    }, [gridSetup, showNotification]);

    useEffect(() => {
        fetchData()
    }, [fetchData, reloadKey]);

    const handleFilterChange = (filter: GridFilterChangeEvent) => {
        setGridSetup({
            ...gridSetup,
            filter: filter.filter,
            ...FIRST_PAGE
        })
    }

    const handleSortChange = (pageProps: any) => {
        setGridSetup({
            ...gridSetup,
            sort: pageProps.sort.length > 0 ? pageProps.sort : DEFAULT_SORT,
        })
    }

    const handlePageChange = (pageProps: any) => {
        setGridSetup({
            ...gridSetup,
            skip: pageProps.page.skip,
            take: pageProps.page.take,
        })
    }

    const handleResendToXero = (id: number) => {
        setLoading(true);

        resendToXero(id)
            .then(() => {
                setGridSetup(INITIAL_GRID_SETUP);

                showNotification({
                    message: "Successfully re-send error invoice to Xero",
                    header: `Success re-send`,
                    type: 'success',
                })
            })
            .catch((error: any) => {
                showNotification({
                    message: resolveMessage(error, "Internal Server Error"),
                    header: "Re-send failed",
                    type: 'danger'
                });
            })
            .finally(() => {
                setLoading(false);
                setReloadKey((currentValue) => currentValue + 1);
            });
    }

    const invoiceCell = (props: GridCellProps) => {
        return (
            <td>
                <div className='fw-bold text-gray-800'>{props.dataItem.invoiceNumber}</div>
                <div className='text-muted fs-8'>{props.dataItem.invoicedName}</div>
            </td>
        )
    }

    const errorTypeCell = (props: GridCellProps) => {
        const errorType = props.dataItem.xeroSendErrorType || 'Mapping'
        const normalizedErrorType = errorType.toLowerCase()
        const badgeClass = normalizedErrorType.includes('mapping')
                ? 'badge-light-danger'
                : normalizedErrorType.includes('api')
                    ? 'badge-light-warning'
                    : 'badge-light-info'

        return (
            <td>
                <span className={`badge ${badgeClass}`}>{normalizedErrorType}</span>
            </td>
        )
    }

    const lastAttemptCell = (props: GridCellProps) => {
        return (
            <td>
                <div className='fw-bold text-gray-800'>{moment(props.dataItem.xeroLastAttemptAtUtc).format("DD MMM, hh:mm a")}</div>
            </td>
        )
    }

    const actionCell = ({ dataItem }: GridCellProps) => {
        return (
            <td>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <button className="btn btn-icon btn-secondary btn-sm" title="View error" onClick={() => {
                        setSelectedData(dataItem);
                        setErrorDetailsModalShow(true);
                    }}>
                        <i className="fas fa-eye"></i>
                    </button>
                    <button className="btn btn-icon btn-dark btn-sm" title="Re-send to Xero" onClick={() => handleResendToXero(dataItem.id)}>
                        <i className="fas fa-redo"></i>
                    </button>
                </div>
            </td>
        )
    }

    return (
        <>
            <div className='card card-custom'>
                <div className='card-body'>
                    <div className='container-fluid px-0 mb-10'>
                        <ErrorDetailsModal
                            show={errorDetailsModalShow}
                            handleClose={() => {
                                setErrorDetailsModalShow(false);
                                setSelectedData(null);
                                fetchData();
                            }}
                            errorData={selectedData}
                        />
                    </div>
                    <div className='App'>
                        <div className='d-flex flex-wrap justify-content-between align-items-start gap-4 mb-8'>
                            <div>
                                <h2 className='fw-bolder text-dark mb-1'>Error Center</h2>
                                <div className='text-muted fs-6'>
                                    Review and retry failed invoice transfers
                                </div>
                            </div>
                        </div>       
                        <ErrorCenterSummaryTiles summary={summary} />
                        {formNotification && <CustomAlert {...formNotification} />}
                        <LoadingPanel show={loading} />
                        <Grid
                            resizable={true}
                            filterable={true}
                            onFilterChange={handleFilterChange}
                            data={tableData}
                            pageable={true}
                            sortable={true}
                            onSortChange={handleSortChange}
                            total={totalRows}
                            onPageChange={handlePageChange}
                            {...gridSetup}
                            filterOperators={DEFAULT_FILTER_OPERATORS}
                        >
                            <Column field='invoiceNumber' title='Invoice' width='150px' cell={invoiceCell} filterCell={TextFilter} />
                            <Column field='invoicedType' title='Type' width='150px' filterCell={TextFilter} />
                            <Column field='xeroSendErrorType' title='Error Type' width='150px' cell={errorTypeCell} filterCell={TextFilter} />
                            <Column field='xeroSendErrorMessage' title='Message' width='650px' sortable={false} filterCell={TextFilter} />
                            <Column field='xeroSendAttemptCount' title='Attempts' width='100px' filterCell={NumberFilter} />
                            <Column field='xeroLastAttemptAtUtc' title='Last Attempt' width='200px' cell={lastAttemptCell} filterCell={DateFilter} />
                            <Column cell={actionCell} width='105px' filterable={false} sortable={false} />
                        </Grid>
                    </div>
                </div>
            </div>
        </>
    )
}

type SummaryTile = {
    label: string,
    value: number,
    icon?: string,
    accentClass?: string,
    iconClass?: string,
    description?: string
}

const ErrorCenterSummaryTiles: React.FC<{ summary: ErrorCenterSummary }> = ({ summary }) => {
    const tiles: SummaryTile[] = [
        {
            label: "Total Errors",
            value: summary.total,
            icon: "fas fa-exclamation-circle",
            accentClass: 'text-dark',
            iconClass: 'bg-light-danger text-danger',
        },
        {
            label: "Mapping Errors",
            value: summary.mapping,
            accentClass: 'text-danger',
            description: "Most common",
        },
        {
            label: "API Errors",
            value: summary.api,
            accentClass: 'text-warning',
            description: "Retriable",
        },
        {
            label: "Config Errors",
            value: summary.config,
            accentClass: 'text-info',
            description: "Need Attention",
        }
    ]

    return (
        <div className='row g-5 mb-8'>
            {tiles.map((tile) => (
                <div className='col-xl-3 col-md-6' key={tile.label}>
                    <div className='border rounded bg-white p-5 h-100 d-flex justify-content-between align-items-center'>
                        <div>
                            <div className='text-gray-700 fs-7 fw-bold mb-1'>{tile.label}</div>
                            <div className={`fs-2 fw-bolder ${tile.accentClass}`}>{tile.value}</div>
                        </div>
                        {tile.iconClass != null ?  
                            <div className={`rounded-circle d-flex align-items-center justify-content-center ${tile.iconClass}`} style={{ width: 42, height: 42 }}>
                                <i className={tile.icon}></i>
                            </div>
                            :
                            <div className="d-flex align-items-center justify-content-center">
                                <div className='text-muted fs-8'>{tile.description}</div>
                            </div>
                        }
                        
                    </div>
                </div>
            ))}
        </div>
    )
}

export default ErrorCenterTable;